import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { attendanceApi, employeeApi, companyApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from 'sonner';
import { Download, CalendarClock, Filter, Check, ChevronsUpDown } from 'lucide-react';
import { exportRowsToExcel } from '@/lib/exportFile';

const STATUS_COLOR = {
  'Present': '#10b981',
  'Late': '#f59e0b',
  'Absent': '#ef4444',
  'Half Day': '#8b5cf6',
  'Leave': '#6366f1',
  'Weekly Off': '#94a3b8',
};

function parseDuration(dur) {
  if (!dur) return 0;
  const parts = String(dur).split(':');
  if (parts.length !== 2) return 0;
  return parseInt(parts[0]) + parseInt(parts[1]) / 60;
}

function formatHrs(hrs) {
  if (!hrs || hrs === 0) return '-';
  const h = Math.floor(hrs);
  const m = Math.round((hrs - h) * 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function AttendanceReportPage() {
  const { user } = useAuth();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [companyFilter, setCompanyFilter] = useState('all');
  const [empFilter, setEmpFilter] = useState('all');
  const [empFilterOpen, setEmpFilterOpen] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companiesCatalog, setCompaniesCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [exporting, setExporting] = useState(false);

  const [totalEmployees, setTotalEmployees] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [allEmployeesForFilter, setAllEmployeesForFilter] = useState([]);

  const normalizeArrayResponse = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.items)) return value.items;
    return [];
  };

  const normalizeListResponse = (value) => {
    if (Array.isArray(value)) return { items: value, total: value.length, page: 1, pages: 1 };
    if (Array.isArray(value?.items)) return { items: value.items, total: value?.total ?? value.items.length, page: value?.page ?? 1, pages: value?.pages ?? 1 };
    if (Array.isArray(value?.employees)) return { items: value.employees, total: value?.total ?? value.employees.length, page: value?.page ?? 1, pages: value?.pages ?? 1 };
    return { items: [], total: 0, page: 1, pages: 1 };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const attendanceParams = { month: selectedMonth, year: selectedYear };
        if (companyFilter !== 'all') attendanceParams.company = companyFilter;
        if (empFilter !== 'all') attendanceParams.employeeId = empFilter;

        const employeeParams = { status: 'Active', page, limit: pageSize };
        if (companyFilter !== 'all') employeeParams.company = companyFilter;
        if (empFilter !== 'all') employeeParams.employeeId = empFilter;

        const [atts, emps, comps] = await Promise.all([
          attendanceApi.get(attendanceParams),
          employeeApi.getAll(employeeParams),
          companyApi.getAll().catch(() => []),
        ]);
        setAttendance(normalizeArrayResponse(atts));
        const normalizedEmps = normalizeListResponse(emps);
        setEmployees(normalizedEmps.items);
        setTotalEmployees(normalizedEmps.total);
        setTotalPages(normalizedEmps.pages);
        setPage((p) => clampPageToTotal(p, normalizedEmps.pages));
        setCompaniesCatalog(normalizeArrayResponse(comps));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedMonth, selectedYear, companyFilter, empFilter, page, pageSize]);

  useEffect(() => {
    const fetchAllEmps = async () => {
      try {
        const emps = await employeeApi.getAll({ status: 'Active' }).catch(() => []);
        setAllEmployeesForFilter(normalizeArrayResponse(emps));
      } catch {}
    };
    fetchAllEmps();
  }, []);

  const companyOptions = useMemo(() => {
    return (companiesCatalog || [])
      .filter((company) => company?.companyId && company?.name)
      .map((company) => ({
        value: company.companyId,
        label: company.name,
      }));
  }, [companiesCatalog]);

  const filteredEmployees = useMemo(() => employees.filter((e) => e?.status === 'Active'), [employees]);

  const viewableEmps = useMemo(
    () => (allEmployeesForFilter.length > 0 ? allEmployeesForFilter : filteredEmployees),
    [allEmployeesForFilter, filteredEmployees]
  );

  const selectedEmpLabel = useMemo(() => {
    if (empFilter === 'all') return 'All Employees';
    const emp = viewableEmps.find((e) => (e.empId || e.id) === empFilter);
    if (!emp) return empFilter;
    return `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || empFilter;
  }, [empFilter, viewableEmps]);

  const daysInMonth = new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate();
  const dayHeaders = useMemo(() => {
    const headers = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1, d);
      headers.push({ day: d, name: DAY_NAMES[date.getDay()], dow: date.getDay() });
    }
    return headers;
  }, [daysInMonth, selectedMonth, selectedYear]);

  const employeeReports = useMemo(() => {
    return filteredEmployees.map(emp => {
      const empId = emp.empId || emp.id;
      const empAtt = attendance.filter(a => a.employeeId === empId);
      const attByDate = {};
      empAtt.forEach(a => { attByDate[a.date] = a; });

      // Determine weekly off days from employee profile
      const weeklyOffDays = (emp.weeklyOffDays || ['Sunday']).map(d => d.toLowerCase());

      const dayData = {};
      let totalPresent = 0, totalAbsent = 0, totalHalfDay = 0, totalLeave = 0, totalWO = 0;
      let totalLateCount = 0, totalDuration = 0, totalOT = 0;

      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${selectedYear}-${selectedMonth}-${String(d).padStart(2, '0')}`;
        const date = new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1, d);
        const dayFullName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        const isWO = weeklyOffDays.includes(dayFullName);
        const rec = attByDate[dateStr];

        let status = '';
        let data = { inTime1: '', outTime1: '', inTime2: '', outTime2: '', duration: '', lateBy: '', earlyBy: '', ot: '', shift: '' };

        if (rec) {
          status = rec.status === 'Present' && rec.isLate ? 'Late' : rec.status;
          data = {
            inTime1: rec.inTime1 || '', outTime1: rec.outTime1 || '',
            inTime2: rec.inTime2 || '', outTime2: rec.outTime2 || '',
            duration: rec.duration || '', lateBy: rec.lateBy || '',
            earlyBy: rec.earlyBy || '', ot: rec.ot || '', shift: rec.shift || '',
          };
          if (rec.status === 'Present') totalPresent++;
          else if (rec.status === 'Absent') totalAbsent++;
          else if (rec.status === 'Half Day') { totalHalfDay++; totalPresent += 0.5; }
          else if (rec.status === 'Leave') totalLeave++;
          else if (rec.status === 'Weekly Off') totalWO++;
          if (rec.isLate) totalLateCount++;
          totalDuration += parseDuration(rec.duration);
          totalOT += parseDuration(rec.ot);
        } else if (isWO) {
          status = 'Weekly Off';
          totalWO++;
        } else if (date <= now) {
          status = '';
        }

        dayData[d] = { status, ...data };
      }

      const avgHrs = totalPresent > 0 ? totalDuration / totalPresent : 0;

      return {
        empId, name: `${emp.firstName} ${emp.lastName}`,
        department: emp.department, designation: emp.designation,
        weeklyOffDays: emp.weeklyOffDays || ['Sunday'],
        dayData,
        summary: { totalPresent, totalAbsent, totalHalfDay, totalLeave, totalWO, totalLateCount, totalDuration, totalOT, avgHrs },
      };
    });
  }, [filteredEmployees, attendance, daysInMonth, selectedMonth, selectedYear]);

  useEffect(() => {
    setPage(1);
  }, [selectedMonth, selectedYear, companyFilter, empFilter]);

  const handleClearFilters = () => {
    const current = new Date();
    setSelectedMonth(String(current.getMonth() + 1).padStart(2, '0'));
    setSelectedYear(String(current.getFullYear()));
    setCompanyFilter('all');
    setEmpFilter('all');
    setPage(1);
  };

  const totalReports = totalEmployees;

  const FIELD_ROWS = [
    { key: 'status', label: 'Status' },
    { key: 'inTime1', label: 'InTime1' },
    { key: 'outTime1', label: 'OutTime1' },
    { key: 'inTime2', label: 'InTime2' },
    { key: 'outTime2', label: 'OutTime2' },
    { key: 'duration', label: 'Duration' },
    { key: 'lateBy', label: 'Late By' },
    { key: 'earlyBy', label: 'Early By' },
    { key: 'ot', label: 'OT' },
    { key: 'shift', label: 'Shift' },
  ];

  const handleExportReport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const dayKeys = Array.from({ length: daysInMonth }, (_, i) => String(i + 1));
      const header = ['Employee', 'Field', ...dayKeys];
      const rows = [];
      employeeReports.forEach((emp) => {
        FIELD_ROWS.forEach((fr) => {
          const row = { Employee: fr.key === 'status' ? `${emp.name} (${emp.empId})` : '', Field: fr.label };
          for (let d = 1; d <= daysInMonth; d++) {
            const val = fr.key === 'status' ? emp.dayData[d]?.status || '' : emp.dayData[d]?.[fr.key] || '';
            row[String(d)] = val;
          }
          rows.push(row);
        });
        rows.push({});
      });

      if (!rows.length) {
        toast.error('No report data to export');
        return;
      }

      exportRowsToExcel(rows, {
        fileName: `AttendanceReport_${selectedYear}_${selectedMonth}`,
        sheetName: 'Attendance Report',
        header,
      });
      toast.success('Report exported');
    } catch (err) {
      toast.error(err?.message || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const statusAbbr = (s) => {
    if (!s) return '';
    if (s === 'Present') return 'P';
    if (s === 'Late') return 'L';
    if (s === 'Absent') return 'A';
    if (s === 'Half Day') return 'HD';
    if (s === 'Leave') return 'LV';
    if (s === 'Weekly Off') return 'WO';
    return s;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="attendance-report-page" className="flex flex-col h-[calc(100vh-2rem)] md:h-[calc(100vh-3rem)] overflow-hidden animate-fadeIn">
      <div className="shrink-0 space-y-3 bg-background z-30">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-[Manrope]">Attendance Report</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">Detailed daily punch-in/out, duration and late metrics</p>
          </div>
          <Button
            variant="outline"
            onClick={handleExportReport}
            disabled={exporting}
            data-testid="export-report-btn"
          >
            <Download className="w-4 h-4 mr-2" /> {exporting ? 'Exporting...' : 'Export Report'}
          </Button>
        </div>

        {/* Filters */}
        <Card className="border border-border shadow-sm">
          <CardContent className="p-3 sm:p-4 flex flex-wrap items-center gap-3">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[140px]" data-testid="report-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => (
                  <SelectItem key={i} value={String(i + 1).padStart(2, '0')}>
                    {new Date(2026, i, 1).toLocaleString('default', { month: 'long' })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[100px]" data-testid="report-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-[180px]" data-testid="report-company"><SelectValue placeholder="Company" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companyOptions.map((company) => (
                  <SelectItem key={company.value} value={company.value}>
                    {company.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Popover open={empFilterOpen} onOpenChange={setEmpFilterOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={empFilterOpen}
                  className="w-[220px] justify-between font-normal"
                  data-testid="report-employee"
                >
                  <span className="flex items-center gap-2 truncate">
                    <Filter className="w-3 h-3 shrink-0" />
                    <span className="truncate">{selectedEmpLabel}</span>
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search employee..." />
                  <CommandList>
                    <CommandEmpty>No employee found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all-employees"
                        onSelect={() => {
                          setEmpFilter('all');
                          setEmpFilterOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            empFilter === 'all' ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        All Employees
                      </CommandItem>
                      {viewableEmps.map((e) => {
                        const empId = e.empId || e.id;
                        const label = `${e.firstName || ''} ${e.lastName || ''}`.trim();
                        return (
                          <CommandItem
                            key={empId}
                            value={`${label} ${empId}`}
                            onSelect={() => {
                              setEmpFilter(empId);
                              setEmpFilterOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                empFilter === empId ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <span className="truncate">{label}</span>
                            <span className="ml-auto text-xs text-muted-foreground font-mono pl-2">
                              {empId}
                            </span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Button
              type="button"
              variant="outline"
              onClick={handleClearFilters}
              data-testid="attendance-report-clear-filters-btn"
            >
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Employee Reports - scrollable content */}
      <div className="flex-1 min-h-0 overflow-auto mt-3 space-y-3">
      {employeeReports.map(emp => (
        <Card key={emp.empId} className="border border-border shadow-sm" data-testid={`report-card-${emp.empId}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="text-base font-[Manrope]">
                  <Link
                    to={`/employees/${emp.empId}`}
                    className="hover:text-[#0047AB] hover:underline"
                    data-testid={`report-employee-link-${emp.empId}`}
                  >
                    {emp.name}
                  </Link>
                </CardTitle>
                <p className="text-xs text-muted-foreground font-mono">
                  {emp.empId} &middot; {emp.department} &middot; {emp.designation}
                  &nbsp;&middot;&nbsp;Off: {emp.weeklyOffDays.join(', ')}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap" data-testid={`summary-${emp.empId}`}>
                {[
                  { l: 'Present', v: emp.summary.totalPresent, c: '#10b981' },
                  { l: 'Absent', v: emp.summary.totalAbsent, c: '#ef4444' },
                  { l: 'HD', v: emp.summary.totalHalfDay, c: '#8b5cf6' },
                  { l: 'WO', v: emp.summary.totalWO, c: '#94a3b8' },
                  { l: 'Late', v: emp.summary.totalLateCount, c: '#f59e0b' },
                  { l: 'Avg', v: formatHrs(emp.summary.avgHrs), c: '#0047AB' },
                  { l: 'OT', v: formatHrs(emp.summary.totalOT), c: '#059669' },
                ].map(s => (
                  <div key={s.l} className="text-center px-2 py-1 rounded bg-muted/50 border min-w-[44px]">
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground leading-tight">{s.l}</p>
                    <p className="text-xs font-bold font-mono" style={{ color: s.c }}>{s.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid={`report-table-${emp.empId}`}>
                <thead>
                  <tr className="bg-muted/60">
                    <th className="sticky left-0 z-10 bg-muted/90 text-left text-[10px] uppercase tracking-widest font-semibold text-muted-foreground px-2 py-1.5 border-b border-r border-border min-w-[70px]">Field</th>
                    {dayHeaders.map(h => {
                      const isWO = emp.dayData[h.day]?.status === 'Weekly Off';
                      return (
                        <th key={h.day} className={`text-center text-[10px] px-1 py-1.5 border-b border-border min-w-[46px] ${isWO ? 'bg-gray-100' : ''}`}>
                          <div className="font-bold">{h.day}</div>
                          <div className="text-[9px] text-muted-foreground font-normal">{h.name}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {FIELD_ROWS.map(fr => (
                    <tr key={fr.key} className={fr.key === 'status' ? 'bg-muted/30' : ''} data-testid={`field-row-${emp.empId}-${fr.key}`}>
                      <td className="sticky left-0 z-10 bg-white text-[10px] uppercase tracking-widest font-semibold text-muted-foreground px-2 py-1 border-b border-r border-border whitespace-nowrap">
                        {fr.label}
                      </td>
                      {dayHeaders.map(h => {
                        const dd = emp.dayData[h.day];
                        const isWO = dd?.status === 'Weekly Off';
                        const val = fr.key === 'status' ? dd?.status || '' : dd?.[fr.key] || '';

                        if (fr.key === 'status') {
                          const abbr = statusAbbr(val);
                          const color = STATUS_COLOR[val] || '#94a3b8';
                          return (
                            <td key={h.day} className={`text-center px-0.5 py-1 border-b border-border ${isWO ? 'bg-gray-50' : ''}`}>
                              {abbr && (
                                <span
                                  className="inline-block text-[10px] font-bold rounded px-1 py-0.5"
                                  style={{ color, backgroundColor: `${color}15` }}
                                >
                                  {abbr}
                                </span>
                              )}
                            </td>
                          );
                        }

                        // Time/duration fields
                        const isEmpty = !val || val === '00:00' || val === '';
                        const isLate = fr.key === 'lateBy' && !isEmpty;
                        const isEarly = fr.key === 'earlyBy' && !isEmpty;
                        const isOT = fr.key === 'ot' && !isEmpty;

                        return (
                          <td key={h.day} className={`text-center px-0.5 py-1 border-b border-border font-mono text-[10px] ${isWO ? 'bg-gray-50' : ''}`}>
                            {isEmpty ? (
                              <span className="text-muted-foreground/30">-</span>
                            ) : (
                              <span className={
                                isLate ? 'text-amber-600 font-semibold' :
                                isEarly ? 'text-orange-500 font-semibold' :
                                isOT ? 'text-emerald-600 font-semibold' :
                                fr.key === 'duration' ? 'font-semibold' : ''
                              }>
                                {val}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      {totalReports === 0 && (
        <Card className="border border-border shadow-sm">
          <CardContent className="p-12 text-center">
            <CalendarClock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No attendance data found</p>
            <p className="text-sm text-muted-foreground mt-1">Import attendance data or adjust filters</p>
          </CardContent>
        </Card>
      )}
      </div>

      {/* Pagination */}
      {totalReports > 0 && (
        <div className="shrink-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-3 pb-1">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages} ({totalReports} employees)
            </p>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-muted-foreground">Show</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[70px] h-8 text-sm" data-testid="report-page-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">per page</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page <= 1}
              data-testid="report-prev-page-btn"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page >= totalPages}
              data-testid="report-next-page-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
